import { api } from './client';
import type { Project, ProjectMilestone, ProjectMilestoneStatus } from '../features/projects/types';
export type { Project, ProjectMilestone, ProjectMilestoneStatus } from '../features/projects/types';

export async function getPublicProjects(): Promise<Project[]> {
  return api.get<Project[]>('/api/projects/public');
}

export async function getProjects(): Promise<Project[]> {
  return api.get<Project[]>('/api/projects');
}

export async function getProject(id: string): Promise<Project | null> {
  try {
    return await api.get<Project>(`/api/projects/${id}`);
  } catch {
    return null;
  }
}

export async function createProject(data: {
  name: string;
  description?: string;
  dtStartDate: string;
  dtEndDate: string;
  fundsNeeded: number;
  fundsRaised?: number;
  isCompleted?: boolean;
  isPublic?: boolean;
  category?: string;
  imageId?: string | null;
}): Promise<Project> {
  return api.post<Project>('/api/projects', data);
}

export async function updateProject(
  id: string,
  data: Partial<{
    name: string;
    description: string;
    dtStartDate: string;
    dtEndDate: string;
    fundsNeeded: number;
    fundsRaised: number;
    isCompleted: boolean;
    isPublic: boolean;
    category: string;
    imageId: string | null;
  }>
): Promise<Project> {
  return api.patch<Project>(`/api/projects/${id}`, data);
}

export async function deleteProject(id: string): Promise<void> {
  return api.delete(`/api/projects/${id}`);
}

export async function getProjectMilestones(projectId: string): Promise<ProjectMilestone[]> {
  return api.get<ProjectMilestone[]>(`/api/projects/${projectId}/milestones`);
}

/** Public (no auth): milestones for a public project only. For use on normal user frontend. */
export async function getPublicProjectMilestones(projectId: string): Promise<ProjectMilestone[]> {
  return api.get<ProjectMilestone[]>(`/api/projects/${projectId}/public-milestones`);
}

export async function createProjectMilestone(
  projectId: string,
  data: {
    title: string;
    description?: string | null;
    status?: ProjectMilestoneStatus;
    order?: number;
    dueDate?: string | null;
  }
): Promise<ProjectMilestone> {
  return api.post<ProjectMilestone>(`/api/projects/${projectId}/milestones`, data);
}

export async function updateProjectMilestone(
  projectId: string,
  milestoneId: string,
  data: Partial<{ title: string; description: string | null; status: ProjectMilestoneStatus; order: number; dueDate: string | null }>
): Promise<ProjectMilestone> {
  return api.patch<ProjectMilestone>(`/api/projects/${projectId}/milestones/${milestoneId}`, data);
}

export async function deleteProjectMilestone(projectId: string, milestoneId: string): Promise<void> {
  return api.delete(`/api/projects/${projectId}/milestones/${milestoneId}`);
}

export function getProjectProgress(fundsRaised: number, fundsNeeded: number): number {
  if (fundsNeeded === 0) return 0;
  return Math.min(Math.round((fundsRaised / fundsNeeded) * 100), 100);
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount);
}

export function isProjectOverdue(endDate: string | Date): boolean {
  return new Date() > new Date(endDate);
}
